'use client';

import { c as _c } from "react/compiler-runtime";
import classnames from 'classnames';
import { useSearchParams } from 'next/navigation';
import { type PropsWithChildren, useEffect, useLayoutEffect, useRef, useState, useTransition } from 'react';
import { VisuallyHidden } from 'react-aria';
import { Dialog } from 'react-aria-components';
import { Loader } from '@module/mdrt-org/shared/components/loader/loader';
import { useUserContext } from '@module/mdrt-org/shared/components/providers/user-context';
import { ScrollableContainer } from '@module/mdrt-org/shared/components/scrollable-container/scrollable-container';
import { usePathname, useRouter } from '@module/mdrt-org/shared/i18n/i18n-navigation';
import { SEARCH_LIMIT_PER_PAGE, SEARCH_PAGE_PARAM_KEY, SEARCH_SAVED_CONTENT_PARAM_KEY, SEARCH_SOURCE_PARAM_KEY, SEARCH_TOPIC_PARAM_KEY, SEARCH_YEAR_PARAM_KEY } from '@module/mdrt-org/shared/utils/constants/learn-search-constants';
import { type LearnSearchResults } from '@module/mdrt-org/shared/utils/data/graphql/data/get-learn-search-results';
import { useBreakpoint } from '@module/mdrt-org/shared/utils/hooks/use-breakpoint';
import { Chip } from '@shared/ui-components/chip/chip';
import { ChipTheme } from '@shared/ui-components/chip/chipTheme';
import { ModalTrigger } from '@shared/ui-components/modal/modal';
import { FilterIcon } from '@shared/ui-components/svgs';
import { GTM_EVENT_NAME_VIEW_SEARCH_RESULTS } from '@shared/utils/constants/gtm';
import { getPathnameWithPartialParameters } from '@shared/utils/helpers/get-pathname-with-partial-parameters';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';
import { useSafePagination } from '@shared/utils/hooks/use-safe-pagination';
import { type SearchCmsContent } from '../../graphql/get-search-cms-content';
import { type SearchPageSearchParameters } from '../../types';
import { getGtmSearchResultsViewEventValues, getValidSearchParameters } from '../../utils/get-gtm-search-results-view-event-values';
import { useAppliedFilterCount } from '../../utils/hooks/use-applied-filter-count';
import { transformFilterValuesToSearchParameters } from '../../utils/transform-filter-values-to-seach-parameters';
import { LoginAlert } from '../login-alert/login-alert';
import { SearchAdvancedFilter } from '../search-advanced-filter/search-advanced-filter';
import { type AdvancedFilterFormData } from '../search-advanced-filter/types/advanced-filter-form-data';
import { useSearchAdvancedFilter } from '../search-advanced-filter/use-search-advanced-filter';
import { SearchBar } from '../search-bar/search-bar';
import { SearchPagination } from '../search-pagination/search-pagination';
import { SearchTypeFilter } from '../search-type-filter/search-type-filter';
import styles from './search-controls.module.scss';
type SearchControlsProps = {
  contentType: null | number;
  pageIndex: number;
  pageTitle: string;
  searchCmsContent: SearchCmsContent;
  searchResults: LearnSearchResults;
};
export const SearchControls = t0 => {
  const $ = _c(78);
  const {
    children,
    contentType,
    pageIndex,
    pageTitle,
    searchCmsContent,
    searchResults
  } = t0;
  const [isPending, startTransition] = useTransition();
  const {
    status
  } = useUserContext();
  const [showSideFilterSection, setShowSideFilterSection] = useState(false);
  const searchParameters = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const {
    breakpoint,
    isLargeDesktop
  } = useBreakpoint();
  const appliedFilterCount = useAppliedFilterCount(status === "authenticated");
  const filtersButtonDesktopRef = useRef(null);
  const [isSearchUpdated, setIsSearchUpdated] = useState(false);
  let t1;
  if ($[0] !== searchResults.facets) {
    t1 = {
      facets: searchResults.facets
    };
    $[0] = searchResults.facets;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const filterState = useSearchAdvancedFilter(t1);
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => () => {
      setShowSideFilterSection(false);
      setIsSearchUpdated(false);
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useLayoutEffect(t2, t3);
  const page = pageIndex + 1;
  const {
    items,
    total
  } = searchResults;
  const totalResults = total || 0;
  let t4;
  if ($[4] !== pathname || $[5] !== router || $[6] !== searchParameters) {
    t4 = parametersToUpdate => {
      setIsSearchUpdated(true);
      const pathWithQuery = getPathnameWithPartialParameters({
        partialParameters: {
          [SEARCH_PAGE_PARAM_KEY]: "",
          ...parametersToUpdate
        },
        pathname,
        searchParameters
      });
      startTransition(() => {
        router.push(pathWithQuery);
      });
    };
    $[4] = pathname;
    $[5] = router;
    $[6] = searchParameters;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const updateSearchParameters = t4;
  const filtersLabel = `${searchCmsContent.searchPage?.FiltersLabel || "Filters"}${appliedFilterCount ? ` (${appliedFilterCount})` : ""}`;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setShowSideFilterSection(false);
      filtersButtonDesktopRef?.current?.focus();
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleDesktopFiltersClose = t5;
  let t6;
  if ($[9] !== updateSearchParameters) {
    t6 = (formData, callback) => {
      const {
        savedContent
      } = formData.savedContent;
      updateSearchParameters({
        [SEARCH_SAVED_CONTENT_PARAM_KEY]: savedContent ? savedContent.toString() : "",
        [SEARCH_SOURCE_PARAM_KEY]: transformFilterValuesToSearchParameters(formData.source),
        [SEARCH_TOPIC_PARAM_KEY]: transformFilterValuesToSearchParameters(formData.topic),
        [SEARCH_YEAR_PARAM_KEY]: transformFilterValuesToSearchParameters(formData.year)
      });
      callback?.();
    };
    $[9] = updateSearchParameters;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleAdvancedFilterSubmit = t6;
  let t7;
  if ($[11] !== appliedFilterCount || $[12] !== updateSearchParameters) {
    t7 = callback_0 => {
      if (appliedFilterCount) {
        updateSearchParameters({
          [SEARCH_SAVED_CONTENT_PARAM_KEY]: "",
          [SEARCH_SOURCE_PARAM_KEY]: "",
          [SEARCH_TOPIC_PARAM_KEY]: "",
          [SEARCH_YEAR_PARAM_KEY]: ""
        });
      }
      callback_0?.();
    };
    $[11] = appliedFilterCount;
    $[12] = updateSearchParameters;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const handleAdvancedFilterReset = t7;
  let t8;
  let t9;
  if ($[14] !== searchParameters) {
    t8 = () => {
      const delay = setTimeout(() => {
        const validSearchParameters = getValidSearchParameters(searchParameters);
        if (Object.values(validSearchParameters).length) {
          sendGtmEvent({
            eventName: GTM_EVENT_NAME_VIEW_SEARCH_RESULTS,
            searchResultsValues: getGtmSearchResultsViewEventValues(validSearchParameters)
          });
        }
      }, 0);
      return () => clearTimeout(delay);
    };
    t9 = [searchParameters];
    $[14] = searchParameters;
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  useEffect(t8, t9);
  const t10 = items?.length;
  let t11;
  if ($[17] !== updateSearchParameters) {
    t11 = nextPage => {
      updateSearchParameters({
        [SEARCH_PAGE_PARAM_KEY]: nextPage > 1 ? String(nextPage - 1) : ""
      });
    };
    $[17] = updateSearchParameters;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== isPending || $[20] !== page || $[21] !== t10 || $[22] !== t11 || $[23] !== totalResults) {
    t12 = {
      currentPageItemsCount: t10,
      isFetching: isPending,
      page,
      pageSize: SEARCH_LIMIT_PER_PAGE,
      setPage: t11,
      totalItemsCount: totalResults
    };
    $[19] = isPending;
    $[20] = page;
    $[21] = t10;
    $[22] = t11;
    $[23] = totalResults;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  useSafePagination(t12);
  let t13;
  if ($[25] !== isPending) {
    t13 = isPending && <Loader allowScroll={true} className={styles.loader} fixedToViewportCenter={true} isInner={true} />;
    $[25] = isPending;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== showSideFilterSection) {
    t14 = classnames(styles.wrapper, {
      [styles["wrapper--filters-open"]]: showSideFilterSection
    });
    $[27] = showSideFilterSection;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== pageTitle) {
    t15 = <VisuallyHidden elementType="h1">{pageTitle}</VisuallyHidden>;
    $[29] = pageTitle;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== searchCmsContent.searchPage || $[32] !== updateSearchParameters) {
    t16 = <SearchBar onSearchQueryChange={updateSearchParameters} searchPageCmsContent={searchCmsContent.searchPage} />;
    $[31] = searchCmsContent.searchPage;
    $[32] = updateSearchParameters;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== breakpoint || $[35] !== filterState || $[36] !== filtersLabel || $[37] !== handleAdvancedFilterReset || $[38] !== handleAdvancedFilterSubmit || $[39] !== isLargeDesktop || $[40] !== searchCmsContent || $[41] !== showSideFilterSection) {
    t17 = <div className={styles["filters-button-wrapper"]}>{isLargeDesktop ? <Chip aria-controls="learn-filters-desktop" aria-expanded={showSideFilterSection} className={styles["toolbar__filter-button"]} icon={<FilterIcon />} label={filtersLabel} onClick={() => setShowSideFilterSection(!showSideFilterSection)} ref={filtersButtonDesktopRef} theme={ChipTheme.Light} /> : <ModalTrigger renderTrigger={t18 => {
        const {
          handleModalOpen
        } = t18;
        return <Chip className={styles["toolbar__filter-button"]} disabled={!breakpoint} icon={<FilterIcon />} label={filtersLabel} onClick={handleModalOpen} theme={ChipTheme.Light} />;
      }}>{handleClose => <Dialog aria-label={filtersLabel}><SearchAdvancedFilter className={classnames(styles.filters, styles["filters--mobile"])} filterState={filterState} isMobile={true} onClose={handleClose} onReset={() => handleAdvancedFilterReset(handleClose)} onSubmit={formData_0 => handleAdvancedFilterSubmit(formData_0, handleClose)} searchCmsContent={searchCmsContent} /></Dialog>}</ModalTrigger>}</div>;
    $[34] = breakpoint;
    $[35] = filterState;
    $[36] = filtersLabel;
    $[37] = handleAdvancedFilterReset;
    $[38] = handleAdvancedFilterSubmit;
    $[39] = isLargeDesktop;
    $[40] = searchCmsContent;
    $[41] = showSideFilterSection;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== contentType || $[44] !== searchCmsContent.searchPage || $[45] !== updateSearchParameters) {
    t18 = <SearchTypeFilter contentType={contentType} onContentTypeChange={updateSearchParameters} searchPageCmsContent={searchCmsContent.searchPage} />;
    $[43] = contentType;
    $[44] = searchCmsContent.searchPage;
    $[45] = updateSearchParameters;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== t17 || $[48] !== t18) {
    t19 = <ScrollableContainer className={styles.toolbar} role="toolbar">{t17}{t18}</ScrollableContainer>;
    $[47] = t17;
    $[48] = t18;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== isSearchUpdated || $[51] !== searchCmsContent.learnPage.signInAlertText || $[52] !== totalResults) {
    t20 = <LoginAlert className={styles.alert} isSearchUpdated={isSearchUpdated} message={searchCmsContent.learnPage.signInAlertText} totalResults={totalResults} />;
    $[50] = isSearchUpdated;
    $[51] = searchCmsContent.learnPage.signInAlertText;
    $[52] = totalResults;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== filterState || $[55] !== handleAdvancedFilterReset || $[56] !== handleAdvancedFilterSubmit || $[57] !== isLargeDesktop || $[58] !== searchCmsContent || $[59] !== showSideFilterSection) {
    t21 = isLargeDesktop && <div className={styles["filters-wrapper"]}><SearchAdvancedFilter className={classnames(styles.filters, {
        [styles["filters--is-open"]]: showSideFilterSection
      })} filterState={filterState} id="learn-filters-desktop" isOpen={showSideFilterSection} onClose={handleDesktopFiltersClose} onReset={handleAdvancedFilterReset} onSubmit={handleAdvancedFilterSubmit} searchCmsContent={searchCmsContent} /></div>;
    $[54] = filterState;
    $[55] = handleAdvancedFilterReset;
    $[56] = handleAdvancedFilterSubmit;
    $[57] = isLargeDesktop;
    $[58] = searchCmsContent;
    $[59] = showSideFilterSection;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  let t22;
  if ($[61] !== pageIndex || $[62] !== searchCmsContent.generic || $[63] !== total || $[64] !== updateSearchParameters) {
    t22 = Boolean(total) && <SearchPagination genericCmsContent={searchCmsContent.generic} onPageChange={updateSearchParameters} pageIndex={pageIndex} totalCount={total} />;
    $[61] = pageIndex;
    $[62] = searchCmsContent.generic;
    $[63] = total;
    $[64] = updateSearchParameters;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  let t23;
  if ($[66] !== children || $[67] !== t14 || $[68] !== t15 || $[69] !== t16 || $[70] !== t19 || $[71] !== t20 || $[72] !== t21 || $[73] !== t22) {
    t23 = <div className={t14}>{t15}{t16}{t19}{t20}{t21}{children}{t22}</div>;
    $[66] = children;
    $[67] = t14;
    $[68] = t15;
    $[69] = t16;
    $[70] = t19;
    $[71] = t20;
    $[72] = t21;
    $[73] = t22;
    $[74] = t23;
  } else {
    t23 = $[74];
  }
  let t24;
  if ($[75] !== t13 || $[76] !== t23) {
    t24 = <>{t13}{t23}</>;
    $[75] = t13;
    $[76] = t23;
    $[77] = t24;
  } else {
    t24 = $[77];
  }
  return t24;
};